// Takeoff performance data of a tail (fleet.performance) used by the EFB performance module.
// Weights are stored in kg in the database; the form shows them in the fleet's weight unit.

export const LBS_PER_KG = 2.20462262185;

// Performance aircraft types the EFB app has a calculation module for.
export const PERFORMANCE_PRODUCTS = [
  { id: "aa.b737.300b1", value: "B737-300 / CFM56-3B1 20K (aa.b737.300b1)" },
];

export const TIRE_SPEED_OPTIONS = [
  { id: "", value: "Not set" },
  { id: "210", value: "210 mph" },
  { id: "225", value: "225 mph" },
];

export const THRUST_TIME_OPTIONS = [
  { id: "", value: "Not set" },
  { id: "5", value: "5 min" },
  { id: "10", value: "10 min" },
];

export const emptyTakeoffPerformance = {
  product: "",
  mlw: "",
  mrw: "",
  bew: "",
  opt_flaps: "",
  min_retraction_height_ft: "",
  tire_speed_mph: "",
  brake_category: "",
  thrust_time_min: "",
  xw_dry: "",
  xw_wet: "",
  xw_slush: "",
  xw_rba: "",
  engine: "",
};

const isBlank = (v) => v === undefined || v === null || v === "";

// Whole kg: the form shows lbs rounded up, so rounding back avoids a drift on every save.
export const toKg = (value, unit) =>
  isBlank(value) || unit !== "lbs"
    ? value
    : Math.round(Number(value) / LBS_PER_KG);

export const toDisplayWeight = (value, unit) =>
  isBlank(value) || unit !== "lbs" ? value : Math.ceil(value * LBS_PER_KG);

// API shape (weights already in the display unit) -> flat string form.
export const performanceToForm = (performance) => {
  if (!performance) return { ...emptyTakeoffPerformance };
  const xw = performance.crosswind_limits_kt || {};
  const str = (v) => (isBlank(v) ? "" : String(v));
  return {
    product: performance.product || "",
    mlw: str(performance.mlw),
    mrw: str(performance.mrw),
    bew: str(performance.bew),
    opt_flaps: (performance.opt_flaps || []).join(", "),
    min_retraction_height_ft: str(performance.min_retraction_height_ft),
    tire_speed_mph: str(performance.tire_speed_mph),
    brake_category: performance.brake_category || "",
    thrust_time_min: str(performance.thrust_time_min),
    xw_dry: str(xw.dry),
    xw_wet: str(xw.wet),
    xw_slush: str(xw.slush),
    xw_rba: str(xw.rba),
    engine: performance.engine || "",
  };
};

export const isTakeoffPerformanceEmpty = (form) =>
  !form || Object.values(form).every(isBlank);

export const parseOptFlaps = (text) =>
  String(text || "")
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map(Number);

// Returns an error message, or "" when the form is valid (or completely empty).
export const validateTakeoffPerformance = (form) => {
  if (isTakeoffPerformanceEmpty(form)) return "";
  if (!form.product) return "Takeoff performance: aircraft type is required";
  if (!(Number(form.mlw) > 0)) return "Takeoff performance: MLW is required";
  if (!(Number(form.mrw) > 0)) return "Takeoff performance: MRW is required";
  if (!isBlank(form.bew) && !(Number(form.bew) > 0))
    return "Takeoff performance: BEW must be a positive number";
  if (!isBlank(form.bew) && Number(form.bew) >= Number(form.mlw))
    return "Takeoff performance: BEW must be below MLW";
  const flaps = parseOptFlaps(form.opt_flaps);
  if (!flaps.length || flaps.some((f) => !(f > 0)))
    return "Takeoff performance: OPT flaps must be numbers like 5, 15";
  return "";
};

// Flat form -> API shape (weights converted to kg). Returns undefined when the form is empty.
export const performanceFromForm = (form, weightUnit) => {
  if (isTakeoffPerformanceEmpty(form)) return undefined;
  const num = (v) => (isBlank(v) ? undefined : Number(v));
  const crosswind = {};
  [
    ["dry", form.xw_dry],
    ["wet", form.xw_wet],
    ["slush", form.xw_slush],
    ["rba", form.xw_rba],
  ].forEach(([key, v]) => {
    if (!isBlank(v)) crosswind[key] = Number(v);
  });
  const result = {
    product: form.product,
    mlw: toKg(num(form.mlw), weightUnit),
    mrw: toKg(num(form.mrw), weightUnit),
    bew: toKg(num(form.bew), weightUnit),
    opt_flaps: parseOptFlaps(form.opt_flaps),
    min_retraction_height_ft: num(form.min_retraction_height_ft),
    tire_speed_mph: num(form.tire_speed_mph),
    brake_category: form.brake_category || undefined,
    thrust_time_min: num(form.thrust_time_min),
    crosswind_limits_kt: Object.keys(crosswind).length ? crosswind : undefined,
    engine: form.engine || undefined,
  };
  Object.keys(result).forEach(
    (k) => result[k] === undefined && delete result[k]
  );
  return result;
};
